import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../apiConfig';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBan, faUserCheck, faUserClock, faStar, faUsers,
    faSearch, faFilter, faWallet, faPhoneAlt, faMapMarkerAlt, 
    faUnlock, faChevronDown, faChevronUp, faSync, faEdit, faCheck, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

const AdminWorkers = () => {
    const socket = io(API_BASE);
    const { t } = useTranslation();
    const [workers, setWorkers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [expandedWorker, setExpandedWorker] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    
    const [editingBalanceId, setEditingBalanceId] = useState(null);
    const [newBalanceValue, setNewBalanceValue] = useState("");

    const getAuthOptions = useCallback((method = 'GET', body = null) => {
        const token = localStorage.getItem('token');
        const options = {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'ngrok-skip-browser-warning': 'true'
            }
        };
        if (body) options.body = JSON.stringify(body);
        return options;
    }, []);

    const fetchWorkers = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await fetch(`${API_BASE}/api/workers`, getAuthOptions());
            if (res.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            const data = await res.json();
            setWorkers(Array.isArray(data) ? data : []);
        } catch (error) { 
            console.error("Fetch error:", error); 
        } finally {
            setTimeout(() => setRefreshing(false), 500);
        }
    }, [getAuthOptions]);

    useEffect(() => { fetchWorkers(); }, [fetchWorkers]);

    const handleUpdateBalance = async (id) => {
        try {
            const res = await fetch(`${API_BASE}/api/workers/update-balance`, 
                getAuthOptions('POST', { id, balance: parseFloat(newBalanceValue) })
            );
            if (res.ok) {
                setWorkers(prev => prev.map(w => w.id === id ? { ...w, balance: newBalanceValue } : w));
                setEditingBalanceId(null);
                setNewBalanceValue("");
            }
        } catch (err) { console.error(err); }
    };

const toggleVerify = async (id, currentVerifyStatus) => {
    // Որոշում ենք նոր ստատուսը (եթե 1 է՝ դարձնում ենք 0, և հակառակը)
    const newStatus = currentVerifyStatus === 1 ? 0 : 1;
    
    // Հարցնում ենք ադմինին
    const confirmMsg = newStatus === 1 ? "Հաստատե՞լ վարպետին:" : "Չեղարկե՞լ հաստատումը:";
    
    if (window.confirm(confirmMsg)) {
        try {
            // ❗ Ուղղված URL-ը համապատասխան քո Backend-ի router.post('/verify-worker', ...)
            const res = await fetch(`${API_BASE}/api/auth/verify-worker`, getAuthOptions('POST', { 
                userId: id, 
                status: newStatus 
            }));
            
            const data = await res.json();

            if (res.ok && data.success) {
                // 1. Թարմացնում ենք տեղական վիճակը (UI)
                setWorkers(prev => prev.map(w => w.id === id ? { ...w, is_verified: newStatus } : w));

                // 2. Socket-ը Backend-ն արդեն ուղարկում է, բայց եթե ուզում ես Frontend-ից էլ ուղարկել՝
                socket.emit('admin_verify_worker', {
                    workerId: id,
                    is_verified: newStatus
                });
                
                console.log("✅ Հաջողությամբ թարմացվեց");
            } else {
                alert(data.error || "Սխալ տեղի ունեցավ");
            }
        } catch (err) { 
            console.error("Verification error:", err); 
            alert("Կապի սխալ սերվերի հետ");
        }
    }
};

    const toggleBlock = async (id, currentBlockedStatus) => {
        const newStatus = currentBlockedStatus === 1 ? 0 : 1;
        if (window.confirm(t('confirm_action'))) {
            try {
                const res = await fetch(`${API_BASE}/api/workers/toggle-block`, getAuthOptions('POST', { id, is_blocked: newStatus }));
                if (res.ok) setWorkers(prev => prev.map(w => w.id === id ? { ...w, is_blocked: newStatus } : w));
            } catch (err) { console.error(err); }
        }
    };

    const filteredWorkers = workers.filter(w => {
        const matchesSearch = (w.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || w.specialization?.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesStatus = (statusFilter === "all" ? true : statusFilter === "blocked" ? w.is_blocked === 1 : statusFilter === "free" ? (w.is_available === 1 && !w.is_blocked) : statusFilter === "busy" ? (w.is_available === 0 && !w.is_blocked) : true);
        return matchesSearch && matchesStatus;
    });

    const stats = {
        total: workers.length,
        free: workers.filter(w => w.is_available === 1 && !w.is_blocked).length,
        blocked: workers.filter(w => w.is_blocked === 1).length,
        totalBalance: workers.reduce((sum, w) => sum + (Number(w.balance) || 0), 0),
        lowBalance: workers.filter(w => (Number(w.balance) || 0) < 1000).length,
        avgRating: (workers.reduce((a, b) => a + (Number(b.rating_score) || 0), 0) / workers.length || 0).toFixed(1)
    };

    return (
        <div className="admin-container">
            <style>{responsiveStyles}</style>
            
            <div className="admin-header">
                <div>
                    <h2 className="page-title">{t('workers_title')}</h2>
                    <p className="page-subtitle">{t('workers_subtitle')}</p>
                </div>
                <button onClick={fetchWorkers} className="refresh-btn" disabled={refreshing}>
                    <FontAwesomeIcon icon={faSync} spin={refreshing} />
                </button>
            </div>

            <div className="stats-grid">
                <StatCard icon={faUsers} label={t('workers_stat_total')} value={stats.total} color="#4318FF" bg="#F4F7FE" />
                <StatCard icon={faWallet} label={t('workers_stat_balance')} value={`${new Intl.NumberFormat('hy-AM').format(stats.totalBalance)} ֏`} color="#05CD99" bg="#E6FFF5" />
                <StatCard icon={faExclamationTriangle} label={t('workers_stat_low_balance')} value={stats.lowBalance} color="#EE5D50" bg="#FFF5F5" />
                <StatCard icon={faStar} label={t('workers_stat_rating')} value={stats.avgRating} color="#FFD700" bg="#FFFDF0" />
            </div>

            <div className="controls-row">
                <div className="search-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input placeholder={t('workers_search_ph')} className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div className="filter-wrapper">
                    <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                    <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">{t('workers_filter_all')}</option>
                        <option value="free">{t('workers_filter_free')}</option>
                        <option value="busy">{t('workers_filter_busy')}</option>
                        <option value="blocked">{t('workers_filter_blocked')}</option>
                    </select>
                </div>
            </div>

            {/* Desktop View */}
            <div className="table-card hide-mobile">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t('workers_th_staff')}</th>
                            <th>{t('workers_th_specialization')}</th>
                            <th>{t('workers_th_balance')}</th>
                            <th>{t('workers_th_status')}</th>
                            <th style={{textAlign: 'right'}}>{t('workers_th_actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredWorkers.map(w => (
                            <WorkerRow 
                                key={w.id} worker={w} t={t}
                                isExpanded={expandedWorker === w.id}
                                onToggle={() => setExpandedWorker(expandedWorker === w.id ? null : w.id)}
                                onBlock={() => toggleBlock(w.id, w.is_blocked)}
                                onVerify={() => toggleVerify(w.id, w.is_verified)}
                                isEditingBalance={editingBalanceId === w.id}
                                setEditingBalanceId={setEditingBalanceId}
                                newBalanceValue={newBalanceValue}
                                setNewBalanceValue={setNewBalanceValue}
                                onUpdateBalance={() => handleUpdateBalance(w.id)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

      {/* Mobile View - Cards Layout */}
<div className="mobile-cards-view show-mobile-only">
    {filteredWorkers.map(w => (
        <div key={w.id} className={`worker-mobile-card ${w.is_blocked ? 'card-blocked' : ''}`}>
            {/* 1. Քարտի գլխամասը (նկար + անուն) */}
            <div className="card-header" onClick={() => setExpandedWorker(expandedWorker === w.id ? null : w.id)}>
                <div className="user-info">
                    <div className="avatar-wrapper">
                      <img 
    src={w.profile_pic 
        ? `https://api.liber.am/${w.profile_pic.replace(/^\//, '')}` 
        : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'
    } 
    className="avatar" 
    alt={w.name || "Worker"} // Լավ է alt-ը դատարկ չթողնել SEO-ի համար
    loading="lazy"           // 1. Բեռնել միայն երբ հասնում ես տվյալ աշխատողին
    decoding="async"         // 2. Նկարի մշակումը թողնել ֆոնային ռեժիմում
    onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
    }}
/>
                        {w.is_available === 1 && !w.is_blocked && <div className="online-badge" />}
                    </div>
                    <div>
                        <div className="user-name">{w.full_name}</div>
                        <div className="spec-badge-small">{w.specialization}</div>
                    </div>
                </div>
                <FontAwesomeIcon icon={expandedWorker === w.id ? faChevronUp : faChevronDown} className="chevron" />
            </div>

            <div className="card-body">
                {/* 2. Հիմնական տվյալներ (բալանս + ստատուս) */}
                <div className="mobile-stat-row">
                    <div className="m-stat" onClick={() => { setEditingBalanceId(w.id); setNewBalanceValue(w.balance); }}>
                        <span className="m-label">{t('workers_th_balance')}</span>
                        <span className={`m-value ${(Number(w.balance) < 1000) ? 'bal-low' : 'bal-ok'}`}>
                            {new Intl.NumberFormat('hy-AM').format(w.balance || 0)} ֏ <FontAwesomeIcon icon={faEdit} size="xs" />
                        </span>
                    </div>
                    <div className="m-stat">
                        <span className="m-label">{t('workers_th_status')}</span>
                        <StatusBadge worker={w} t={t} />
                    </div>
                </div>

                {/* 3. Բացվող բաժին (Հեռախոս + Idram + Քարտ) */}
                {expandedWorker === w.id && (
                    <div className="mobile-expanded-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div className="m-details-text">
                            <div className="m-detail"><FontAwesomeIcon icon={faPhoneAlt} /> {w.phone || '---'}</div>
                            <div className="m-detail"><FontAwesomeIcon icon={faMapMarkerAlt} /> {w.address || '---'}</div>
                            <div className="m-detail"><FontAwesomeIcon icon={faStar} color="#FFD700" /> {w.rating_score || 0} / 5</div>
                        </div>

                        {/* Idram-ի վիջեթը մոբայլում */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div className="idram-widget" style={{ transform: 'scale(0.9)' }}>
                                <div className="idram-logo-text">idram <span className="idram-logo-dot"></span></div>
                                <div className="idram-info-label">Հաշվի համար</div>
                                <div className="idram-phone-number">{w.idram_number || '---'}</div>
                            </div>
                        </div>

                        {/* Բանկային քարտը մոբայլում */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div className="mobile-card-wrapper" style={{ width: '100%', display: 'flex', justifyContent: 'center', transform: 'scale(0.9)' }}>
                                <PaymentCard data={w} />
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. Գործողությունների կոճակներ */}
                <div className="card-actions-row" style={{ marginTop: '15px' }}>
                    <button onClick={() => toggleVerify(w.id, w.is_verified)} className={`m-act-btn ${w.is_verified ? 'v-ok' : 'v-pending'}`}>
                        <FontAwesomeIcon icon={w.is_verified ? faUserCheck : faUserClock} />
                    </button>
                    <button onClick={() => toggleBlock(w.id, w.is_blocked)} className={`m-act-btn ${w.is_blocked ? 'b-ok' : 'b-danger'}`}>
                        <FontAwesomeIcon icon={w.is_blocked ? faUnlock : faBan} />
                    </button>
                    {editingBalanceId === w.id && (
                        <div className="m-balance-edit">
                            <input type="number" value={newBalanceValue} onChange={(e) => setNewBalanceValue(e.target.value)} autoFocus />
                            <button onClick={() => handleUpdateBalance(w.id)}><FontAwesomeIcon icon={faCheck} /></button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    ))}
</div>
        </div>
    );
};

// ... StatCard, WorkerRow, DetailItem, StatusBadge բաղադրիչները մնում են նույնը (ինչպես քո սկզբնական կոդում) ...

const StatCard = ({ icon, label, value, color, bg }) => (
    <div className="stat-card">
        <div className="stat-icon" style={{backgroundColor: bg, color: color}}>
            <FontAwesomeIcon icon={icon} />
        </div>
        <div>
            <p className="stat-label">{label}</p>
            <h3 className="stat-value">{value}</h3>
        </div>
    </div>
);

const WorkerRow = ({ worker, isExpanded, onToggle, onBlock, onVerify, t, isEditingBalance, setEditingBalanceId, newBalanceValue, setNewBalanceValue, onUpdateBalance }) => (
    <>
        <tr onClick={onToggle} className={`worker-tr ${worker.is_blocked ? 'is-blocked' : ''}`}>
            <td>
                <div className="user-info">
                    <div className="avatar-wrapper">
                       <img 
  src={worker.profile_pic 
    ? `https://api.liber.am/${worker.profile_pic.replace(/^\//, '')}` 
    : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'
  } 
  className="avatar" 
  alt=""
  onError={(e) => {
    e.target.onerror = null;
    e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
  }}
/>
                        {worker.is_available === 1 && !worker.is_blocked && <div className="online-badge" />}
                    </div>
                    <div>
                        <div className="user-name">{worker.full_name}</div>
                        <div className="user-id">ID: {worker.id}</div>
                    </div>
                </div>
            </td>
            <td><div className="spec-badge">{worker.specialization || t('workers_default_spec')}</div></td>
            <td onClick={(e) => e.stopPropagation()}>
                {isEditingBalance ? (
                    <div className="edit-balance-container">
                        <input autoFocus type="number" className="balance-input-field" value={newBalanceValue} onChange={(e) => setNewBalanceValue(e.target.value)} />
                        <button onClick={onUpdateBalance} className="save-bal-btn"><FontAwesomeIcon icon={faCheck} /></button>
                    </div>
                ) : (
                    <div className="balance-display" onClick={() => { setEditingBalanceId(worker.id); setNewBalanceValue(worker.balance); }}>
                        <span className={`bal-amt ${(Number(worker.balance) < 1000) ? 'bal-low' : 'bal-ok'}`}>
                            {new Intl.NumberFormat('hy-AM').format(worker.balance || 0)} ֏
                        </span>
                        <FontAwesomeIcon icon={faEdit} className="edit-icon-small" />
                    </div>
                )}
            </td>
            <td><StatusBadge worker={worker} t={t} /></td>
            <td style={{textAlign: 'right'}}>
                <div className="action-btns-group">
                    <button onClick={(e) => { e.stopPropagation(); onVerify(); }} className={`act-btn-circle ${worker.is_verified ? 'v-ok' : 'v-pending'}`}>
                        <FontAwesomeIcon icon={worker.is_verified ? faUserCheck : faUserClock} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onBlock(); }} className={`act-btn-circle ${worker.is_blocked ? 'b-ok' : 'b-danger'}`}>
                        <FontAwesomeIcon icon={worker.is_blocked ? faUnlock : faBan} />
                    </button>
                    <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="chevron-icon" />
                </div>
            </td>
        </tr>
       
{isExpanded && (
    <tr className="expanded-tr">
        <td colSpan="5">
            <div className="admin-expanded-wrapper">
                {/* 1. Ձախից՝ Տեքստային ինֆո */}
                <div className="expanded-info-text">
                    <DetailItem icon={faPhoneAlt} label="Հեռախոս" value={worker.phone} />
                    <DetailItem icon={faMapMarkerAlt} label="Հասցե" value={worker.address} />
                    <DetailItem icon={faStar} label="Rating" value={`${worker.rating_score || 0} / 5`} />
                </div>

                {/* 2. Մեջտեղում՝ Idram Վիդջեթ */}
                <div className="idram-widget">
                    <div className="idram-logo-box">
                        <div className="idram-logo-text">
                            idram <span className="idram-logo-dot"></span>
                        </div>
                    </div>
                    <div className="idram-info-label">Հաշվի համար (Հեռախոս)</div>
                    <div className="idram-phone-number">{worker.idram_number || '---'}</div>
                </div>

                {/* 3. Աջից՝ Բանկային Քարտը */}
                <div className="expanded-visual-card">
                    <PaymentCard data={worker} />
                </div>
            </div>
        </td>
    </tr>
)}
       
    </>
);

const DetailItem = ({ icon, label, value }) => (
    <div className="detail-item">
        <div className="detail-icon-box"><FontAwesomeIcon icon={icon} /></div>
        <div>
            <div className="detail-label">{label}</div>
            <div className="detail-value">{value || '---'}</div>
        </div>
    </div>
);

const StatusBadge = ({ worker, t }) => {
    if (worker.is_blocked) return <span className="badge b-blocked">{t('workers_status_blocked')}</span>;
    return worker.is_available ? 
        <span className="badge b-free">{t('workers_status_free')}</span> : 
        <span className="badge b-busy">{t('workers_status_busy')}</span>;
};
const PaymentCard = ({ data }) => {
    return (
        <div className="luxury-credit-card">
            <div className="card-top">
                <div className="bank-name">{data.bank_name || 'BANK NAME'}</div>
                <div className="card-chip"></div>
            </div>
            
            <div className="card-mid">
                {data.card_number 
                    ? data.card_number.replace(/(\d{4})/g, '$1 ').trim() 
                    : '**** **** **** ****'}
            </div>

            <div className="card-bottom-flex">
                <div className="holder-info">
                    <small>CARD HOLDER</small>
                    <div className="h-name">{data.card_holder_name || 'FULL NAME'}</div>
                </div>
                <div className="card-brand-mark">
                    <div className="circle-1"></div>
                    <div className="circle-2"></div>
                </div>
            </div>
        </div>
    );
};
const responsiveStyles = `
    .admin-container { padding: 30px; background-color: #F4F7FE; min-height: 100vh; font-family: 'Plus Jakarta Sans', sans-serif; }
    .admin-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
    .page-title { font-size: 24px; font-weight: 800; color: #1B2559; margin: 0; }
    .page-subtitle { font-size: 14px; color: #A3AED0; margin-top: 4px; }
    .refresh-btn { width: 40px; height: 40px; border-radius: 12px; border: none; background: #fff; color: #4318FF; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
    
    .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 30px; }
    .stat-card { background: #fff; padding: 20px; border-radius: 20px; display: flex; align-items: center; gap: 15px; box-shadow: 0 10px 30px rgba(0,0,0,0.02); }
    .stat-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 18px; }
    .stat-label { font-size: 12px; color: #A3AED0; margin: 0; font-weight: 600; }
    .stat-value { font-size: 18px; font-weight: 800; margin: 0; color: #1B2559; }

    .controls-row { display: flex; gap: 15px; margin-bottom: 25px; flex-wrap: wrap; }
    .search-wrapper { position: relative; flex: 2; min-width: 250px; }
    .search-icon { position: absolute; left: 16px; top: 14px; color: #A3AED0; }
    .search-input { width: 100%; padding: 12px 16px 12px 45px; border-radius: 16px; border: 1px solid #E0E5F2; outline: none; box-sizing: border-box; background: #fff; }
    .filter-wrapper { position: relative; flex: 1; min-width: 150px; }
    .filter-icon { position: absolute; left: 14px; top: 14px; color: #A3AED0; z-index: 1; }
    .filter-select { width: 100%; padding: 12px 15px 12px 35px; border-radius: 16px; border: 1px solid #E0E5F2; background: #fff; outline: none; }

    .table-card { background: #fff; border-radius: 24px; padding: 10px; box-shadow: 0 20px 40px rgba(0,0,0,0.02); }
    .admin-table { width: 100%; border-collapse: collapse; }
    .admin-table th { padding: 18px 20px; text-align: left; color: #A3AED0; font-size: 12px; font-weight: 800; text-transform: uppercase; border-bottom: 1px solid #F4F7FE; }
    .admin-table td { padding: 16px 20px; border-bottom: 1px solid #F4F7FE; }
    .worker-tr:hover { background-color: #F9FAFD; cursor: pointer; }

    .user-info { display: flex; align-items: center; gap: 12px; }
    .avatar { width: 40px; height: 40px; border-radius: 10px; object-fit: cover; }
    .avatar-wrapper { position: relative; }
    .online-badge { position: absolute; bottom: -2px; right: -2px; width: 10px; height: 10px; border-radius: 50%; background: #05CD99; border: 2px solid #fff; }
    .user-name { font-weight: 700; color: #1B2559; font-size: 14px; }
    .spec-badge { background: #F4F7FE; padding: 6px 12px; border-radius: 10px; font-size: 12px; color: #4318FF; font-weight: 700; }
    
    .badge { padding: 6px 12px; border-radius: 10px; font-size: 11px; font-weight: 800; display: inline-block; }
    .b-free { background: #E6FFF5; color: #05CD99; }
    .b-busy { background: #FFF9F0; color: #FFB547; }
    .b-blocked { background: #FFF5F5; color: #EE5D50; }

    .act-btn-circle { width: 32px; height: 32px; border: none; border-radius: 50%; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; margin-left: 5px; }
    .v-ok { background: #E6FFF5; color: #05CD99; }
    .v-pending { background: #FFF9F0; color: #FFB547; }
    .b-ok { background: #F4F7FE; color: #4318FF; }
    .b-danger { background: #FFF5F5; color: #EE5D50; }
.expanded-details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    padding: 20px;
    background: #F8F9FD;
    border-radius: 15px;
    margin-top: 10px;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.detail-icon-box {
    width: 36px;
    height: 36px;
    background: #fff;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4318FF;
    box-shadow: 0 4px 10px rgba(0,0,0,0.03);
}
    /* MOBILE CARDS STYLES */
    .mobile-cards-view { display: none; flex-direction: column; gap: 15px; }
    .worker-mobile-card { background: #fff; border-radius: 20px; padding: 15px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: 1px solid transparent; }
    .card-blocked { border-left: 5px solid #EE5D50; background: #FFF8F8; }
    .card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
    .spec-badge-small { font-size: 11px; color: #4318FF; font-weight: 600; }
    .mobile-stat-row { display: grid; grid-template-columns: 1fr 1fr; background: #F4F7FE; padding: 10px; border-radius: 12px; margin-bottom: 12px; }
    .m-stat { display: flex; flex-direction: column; }
    .m-label { font-size: 10px; color: #A3AED0; text-transform: uppercase; font-weight: 700; }
    .m-value { font-size: 13px; font-weight: 800; }
    .mobile-expanded-content { padding: 10px 0; border-top: 1px dashed #E0E5F2; margin-bottom: 10px; }
    .m-detail { font-size: 12px; color: #1B2559; margin-bottom: 5px; display: flex; gap: 8px; align-items: center; }
    .card-actions-row { display: flex; gap: 10px; position: relative; }
    .m-act-btn { flex: 1; padding: 8px; border-radius: 10px; border: none; font-size: 16px; cursor: pointer; }
    .m-balance-edit { position: absolute; bottom: 0; left: 0; right: 0; background: #fff; display: flex; gap: 5px; padding: 2px; }
    .m-balance-edit input { flex: 1; padding: 8px; border-radius: 8px; border: 1px solid #4318FF; }

    .hide-mobile { display: block; }
    .show-mobile-only { display: none; }
/* --- LUXURY CARD DESIGN --- */
    .luxury-credit-card {
        width: 100%;
        max-width: 340px;
        height: 190px;
        background: linear-gradient(135deg, #0f0f0f 0%, #232526 100%);
        border-radius: 20px;
        padding: 22px;
        color: white;
        box-shadow: 0 15px 35px rgba(0,0,0,0.4);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        position: relative;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* Փայլի էֆեկտ քարտի վրա */
    .luxury-credit-card::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%);
        transform: rotate(30deg);
    }

    .card-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        z-index: 1;
    }

    .bank-name {
        font-size: 1.1rem;
        font-weight: 800;
        text-transform: uppercase;
        background: linear-gradient(to right, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        letter-spacing: 1.5px;
        filter: drop-shadow(0 2px 2px rgba(0,0,0,0.3));
    }

    .card-chip {
        width: 42px;
        height: 32px;
        background: linear-gradient(135deg, #d4af37 0%, #f9d976 100%);
        border-radius: 7px;
        position: relative;
    }

    .card-mid {
        font-size: 1.35rem;
        letter-spacing: 3px;
        text-align: center;
        margin: 10px 0;
        color: #fff;
        font-family: 'Courier New', monospace;
        font-weight: bold;
        text-shadow: 0 2px 5px rgba(0,0,0,0.8);
        z-index: 1;
    }

    .card-bottom-flex {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        z-index: 1;
    }

    .holder-info small, .idram-badge-mini small {
        font-size: 8px;
        color: #A3AED0;
        text-transform: uppercase;
        display: block;
        letter-spacing: 1px;
    }

    .h-name {
        font-size: 14px;
        font-weight: 600;
        color: #fff;
        margin-top: 2px;
    }

    .idram-badge-mini {
        text-align: right;
    }

    .i-val {
        font-size: 14px;
        font-weight: 700;
        color: #ff6b00; /* Idram Orange */
        text-shadow: 0 0 10px rgba(255, 107, 0, 0.2);
    }

    /* Admin Expanded Section */
    .admin-expanded-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 25px;
        background: #F4F7FE;
        border-radius: 0 0 20px 20px;
        gap: 40px;
    }
        /* IDRAM STANDALONE WIDGET */
    .idram-widget {
        background: #ffffff;
        border: 2px solid #ff6b00;
        border-radius: 20px;
        padding: 20px;
        width: 100%;
        max-width: 260px;
        height: 190px; /* Նույն բարձրությունը ինչ քարտինը */
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        box-shadow: 0 10px 25px rgba(255, 107, 0, 0.15);
        position: relative;
        text-align: center;
    }

    .idram-logo-box {
        margin-bottom: 15px;
    }

    .idram-logo-text {
        font-size: 28px;
        font-weight: 900;
        color: #ff6b00;
        letter-spacing: -1.5px;
        display: flex;
        align-items: center;
        gap: 2px;
    }

    .idram-logo-dot {
        width: 8px;
        height: 8px;
        background: #ff6b00;
        border-radius: 50%;
        margin-top: 10px;
    }

    .idram-info-label {
        font-size: 10px;
        color: #A3AED0;
        font-weight: 700;
        text-transform: uppercase;
        margin-bottom: 5px;
    }

    .idram-phone-number {
        font-size: 20px;
        font-weight: 800;
        color: #1B2559;
        letter-spacing: 0.5px;
    }

    /* Լրացուցիչ դիզայն քարտի համար (Brand mark) */
    .card-brand-mark {
        display: flex;
        position: relative;
        width: 40px;
        height: 25px;
    }
    .circle-1 { width: 25px; height: 25px; background: rgba(255,255,255,0.2); border-radius: 50%; }
    .circle-2 { width: 25px; height: 25px; background: rgba(255,255,255,0.1); border-radius: 50%; position: absolute; left: 15px; }

    @media (max-width: 900px) {
        .admin-expanded-wrapper {
            flex-direction: column;
            text-align: center;
        }
    }
    @media (max-width: 768px) {
        .hide-mobile { display: none !important; }
        .show-mobile-only { display: flex !important; }
        .admin-container { padding: 15px; }
        .stats-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
        .page-title { font-size: 20px; }
    }
`;


export default AdminWorkers;